import React, { useState, useEffect } from 'react';
import { Dialog, Button, Input, FlexBox, BusyIndicator, Icon } from '@ui5/webcomponents-react';
import { initGuestAccess, verifyGuestAccess } from './api';

// Per-source copy
const SOURCE_CONFIG = {
  airbnb: {
    title:       'Verify your Airbnb Stay',
    hint:        'Enter the last 4 digits of the phone number on your Airbnb reservation.',
    placeholder: 'Last 4 digits of phone',
    inputType:   'Tel',
  },
  vrbo: {
    title:       'Verify your VRBO Stay',
    hint:        'Enter the last name on your VRBO reservation.',
    placeholder: 'Last name',
    inputType:   'Text',
  },
  google: {
    title:       'Verify your Stay',
    hint:        null, // auto-verified — no input shown
    placeholder: '',
    inputType:   'Text',
  },
  long: {
    title:       'Smart Home Controls Access',
    hint:        'Enter the access password provided by your host.',
    placeholder: 'Access password',
    inputType:   'Password',
  },
  unknown: {
    title:       'Verify your Stay',
    hint:        'Enter the last 4 digits of your phone number, or your last name if you booked on VRBO.',
    placeholder: 'Phone last 4 or last name',
    inputType:   'Text',
  },
};

function GuestVerifyDialog({ open, uuid, onVerified, onClose }) {
  const [step, setStep]             = useState('loading'); // 'loading'|'input'|'checking'|'error'
  const [rentalType, setRentalType] = useState('short');
  const [bookingSource, setBookingSource] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [errorMsg, setErrorMsg]     = useState('');

  // Kick off initGuestAccess every time the dialog opens
  useEffect(() => {
    if (!open) return;
    setStep('loading');
    setVerifyCode('');
    setErrorMsg('');
    setBookingSource(null);

    initGuestAccess(uuid)
      .then(result => {
        if (result.error) {
          setErrorMsg(result.error);
          setStep('error');
          return;
        }
        const src = result.bookingSource || null;
        setRentalType(result.rentalType || 'short');
        setBookingSource(src);

        // Google calendar bookings have no verifiable guest info — auto-verify
        if (src === 'google') {
          setStep('checking');
          verifyGuestAccess(uuid, '')
            .then(vr => {
              if (vr.error) { setErrorMsg(vr.error); setStep('error'); }
              else { onVerified(''); }   // Google: no code, but pass empty string for consistency
            })
            .catch(() => { setErrorMsg('Unable to connect. Please try again.'); setStep('error'); });
        } else {
          setStep('input');
        }
      })
      .catch(() => {
        setErrorMsg('Unable to connect. Please try again.');
        setStep('error');
      });
  }, [open, uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSubmit() {
    if (!verifyCode.trim()) return;
    setStep('checking');
    setErrorMsg('');
    try {
      const result = await verifyGuestAccess(uuid, verifyCode.trim());
      if (result.error) {
        setErrorMsg(result.error);
        setStep('input');
      } else {
        onVerified(verifyCode.trim());  // pass code up so App can store it
      }
    } catch (e) {
      setErrorMsg('Unable to connect. Please try again.');
      setStep('input');
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') handleSubmit();
  }

  // Pick copy based on source (long-term overrides everything)
  const configKey = rentalType === 'long' ? 'long'
                  : (bookingSource && SOURCE_CONFIG[bookingSource] ? bookingSource : 'unknown');
  const config = SOURCE_CONFIG[configKey];

  const busy = step === 'loading' || step === 'checking';

  return (
    <Dialog
      open={open}
      headerText={config.title}
      onAfterClose={onClose}
      footer={
        step === 'input' ? (
          <FlexBox gap="0.5rem" style={{ padding: '0.5rem 1rem' }}>
            <Button
              design="Emphasized"
              onClick={handleSubmit}
              disabled={!verifyCode.trim()}
            >
              Continue
            </Button>
            <Button design="Transparent" onClick={onClose}>Cancel</Button>
          </FlexBox>
        ) : step === 'error' ? (
          <FlexBox style={{ padding: '0.5rem 1rem' }}>
            <Button design="Transparent" onClick={onClose}>Close</Button>
          </FlexBox>
        ) : null
      }
    >
      <div className="verify-dialog-body">
        {busy && (
          <FlexBox justifyContent="Center" style={{ padding: '1.5rem' }}>
            <BusyIndicator active size="Medium" />
          </FlexBox>
        )}

        {step === 'error' && (
          <FlexBox direction="Column" alignItems="Center" gap="0.75rem" style={{ padding: '0.5rem 0' }}>
            <Icon name="error" style={{ fontSize: '2rem', color: 'var(--sapNegativeColor, #b00)' }} />
            <span className="verify-error-text">{errorMsg}</span>
          </FlexBox>
        )}

        {step === 'input' && (
          <>
            {config.hint && <p className="verify-hint">{config.hint}</p>}
            {errorMsg   && <p className="verify-error-text">{errorMsg}</p>}
            <Input
              placeholder={config.placeholder}
              value={verifyCode}
              type={config.inputType}
              style={{ width: '100%', fontSize: '16px' }}
              onInput={e => setVerifyCode(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </>
        )}
      </div>
    </Dialog>
  );
}

export default GuestVerifyDialog;
